import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, Timestamp, limit, startAfter, writeBatch, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { firebaseCache } from "../../utils/firebaseCache";

// Async thunks
export const fetchMeetings = createAsyncThunk(
  "meetings/fetchMeetings",
  async (params, { rejectWithValue }) => {
    try {
      const userId = typeof params === 'string' ? params : params?.userId;

      if (!userId) {
        throw new Error("fetchMeetings called without userId");
      }

      // Check if this is a paginated call
      const isPaginated = params?.pageSize || params?.lastDoc;

      if (isPaginated) {
        // Use the paginated version
        const cacheKey = firebaseCache.getMeetingCacheKey(
          userId,
          params.pageSize || 20,
          params.lastDoc ? 'paged' : 'first'
        );

        // Try cache first
        const cachedData = firebaseCache.get(cacheKey);
        if (cachedData) {
          return cachedData;
        }

        const meetingsRef = collection(db, "meetings");
        let q = query(
          meetingsRef,
          where("userId", "==", userId),
          orderBy('meetingDate', 'asc'),
          limit(params.pageSize || 20)
        );

        if (params.lastDoc) {
          q = query(q, startAfter(params.lastDoc));
        }

        const querySnapshot = await getDocs(q);
        const meetings = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          let serializableDate;
          if (data.meetingDate?.seconds) {
            serializableDate = {
              seconds: data.meetingDate.seconds,
              nanoseconds: data.meetingDate.nanoseconds,
            };
          } else if (data.meetingDate?.toDate) {
            const timestamp = data.meetingDate;
            serializableDate = {
              seconds: timestamp.seconds,
              nanoseconds: timestamp.nanoseconds,
            };
          } else if (data.meetingDate instanceof Date) {
            const firestoreTimestamp = Timestamp.fromDate(data.meetingDate);
            serializableDate = {
              seconds: firestoreTimestamp.seconds,
              nanoseconds: firestoreTimestamp.nanoseconds,
            };
          } else {
            serializableDate = data.meetingDate;
          }

          meetings.push({
            id: doc.id,
            ...data,
            meetingDate: serializableDate,
          });
        });

        const result = {
          meetings,
          lastDoc: querySnapshot.docs.length > 0 ? querySnapshot.docs[querySnapshot.docs.length - 1] : null,
          hasMore: querySnapshot.docs.length === (params.pageSize || 20)
        };

        // Cache for 24 hours (data only changes on writes, invalidation ensures freshness)
        firebaseCache.set(cacheKey, result, 24 * 60 * 60 * 1000);

        return result;
      }

      // This is a call for all meetings (non-paginated)
      const cacheKey = `meetings_all_${userId}`;

      // Try cache first
      const cachedData = firebaseCache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      // Query without orderBy to avoid composite index requirement
      const q = query(
        collection(db, "meetings"),
        where("userId", "==", userId)
      );

      const querySnapshot = await getDocs(q);
      const meetings = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('🔥 Raw Firebase document data:', {
          id: doc.id,
          meetingDate: data.meetingDate,
          meetingDateType: typeof data.meetingDate,
          hasSeconds: data.meetingDate?.seconds,
          hasNanoseconds: data.meetingDate?.nanoseconds,
          isTimestamp: data.meetingDate?.toDate,
          fullData: data
        });

        // Handle different timestamp formats from Firebase
        let serializableDate;
        if (data.meetingDate?.seconds) {
          // Already in serializable format
          serializableDate = {
            seconds: data.meetingDate.seconds,
            nanoseconds: data.meetingDate.nanoseconds,
          };
        } else if (data.meetingDate?.toDate) {
          // Firestore Timestamp object
          const timestamp = data.meetingDate;
          serializableDate = {
            seconds: timestamp.seconds,
            nanoseconds: timestamp.nanoseconds,
          };
        } else if (data.meetingDate instanceof Date) {
          // JavaScript Date object - convert to Firestore Timestamp first
          const firestoreTimestamp = Timestamp.fromDate(data.meetingDate);
          serializableDate = {
            seconds: firestoreTimestamp.seconds,
            nanoseconds: firestoreTimestamp.nanoseconds,
          };
        } else {
          console.warn('Unknown meetingDate format:', data.meetingDate);
          serializableDate = data.meetingDate;
        }

        // Handle createdAt and updatedAt timestamps
        let serializableCreatedAt, serializableUpdatedAt;

        if (data.createdAt?.seconds) {
          serializableCreatedAt = {
            seconds: data.createdAt.seconds,
            nanoseconds: data.createdAt.nanoseconds,
          };
        } else if (data.createdAt?.toDate) {
          const timestamp = data.createdAt;
          serializableCreatedAt = {
            seconds: timestamp.seconds,
            nanoseconds: timestamp.nanoseconds,
          };
        } else {
          serializableCreatedAt = data.createdAt;
        }

        if (data.updatedAt?.seconds) {
          serializableUpdatedAt = {
            seconds: data.updatedAt.seconds,
            nanoseconds: data.updatedAt.nanoseconds,
          };
        } else if (data.updatedAt?.toDate) {
          const timestamp = data.updatedAt;
          serializableUpdatedAt = {
            seconds: timestamp.seconds,
            nanoseconds: timestamp.nanoseconds,
          };
        } else {
          serializableUpdatedAt = data.updatedAt;
        }

        meetings.push({
          id: doc.id,
          ...data,
          meetingDate: serializableDate,
          createdAt: serializableCreatedAt,
          updatedAt: serializableUpdatedAt,
        });
      });

      // Sort meetings by date in JavaScript (client-side sorting)
      meetings.sort((a, b) => {
        const dateA = new Date(a.meetingDate.seconds * 1000);
        const dateB = new Date(b.meetingDate.seconds * 1000);
        return dateA - dateB;
      });

      console.log('✅ Processed and sorted meetings for Redux:', meetings.length, 'meetings');

      // Cache for 24 hours (data only changes on writes, invalidation ensures freshness)
      firebaseCache.set(cacheKey, meetings, 24 * 60 * 60 * 1000);

      return meetings;
    } catch (error) {
      console.error('❌ Error fetching meetings:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * fetchMeetingsForMultipleUsers - Fetch meetings from multiple staff members
 * Used by admins to view aggregated meetings from all linked staff
 */
export const fetchMeetingsForMultipleUsers = createAsyncThunk(
  'meetings/fetchMeetingsForMultipleUsers',
  async (userIds, { rejectWithValue }) => {
    try {
      if (!userIds || userIds.length === 0) {
        console.log('No user IDs provided, returning empty array');
        return [];
      }

      console.log(`Fetching meetings for ${userIds.length} user(s):`, userIds);

      // Create aggregated cache key
      const cacheKey = `meetings_aggregated_${userIds.slice().sort().join('_')}`;

      // Try cache first
      const cachedData = firebaseCache.get(cacheKey);
      if (cachedData) {
        console.log(`✅ Returning ${cachedData.length} meetings from cache (key: ${cacheKey})`);
        return cachedData;
      }

      // Fetch meetings for each user in parallel
      const meetingsPromises = userIds.map(async (userId) => {
        try {
          const q = query(
            collection(db, "meetings"),
            where("userId", "==", userId)
          );
          const querySnapshot = await getDocs(q);
          const meetings = [];

          querySnapshot.forEach((doc) => {
            const data = doc.data();

            // Convert all Firestore Timestamps to ISO strings for Redux serialization
            const convertTimestamp = (timestamp) => {
              if (!timestamp) return null;
              if (timestamp.toDate) return timestamp.toDate().toISOString();
              if (timestamp.seconds) return new Date(timestamp.seconds * 1000).toISOString();
              if (timestamp instanceof Date) return timestamp.toISOString();
              return timestamp;
            };

            meetings.push({
              id: doc.id,
              ...data,
              meetingDate: convertTimestamp(data.meetingDate),
              createdAt: convertTimestamp(data.createdAt),
              updatedAt: convertTimestamp(data.updatedAt),
              agentId: userId, // Add agentId to track which staff owns this meeting
            });
          });

          console.log(`Fetched ${meetings.length} meetings for user ${userId}`);
          return meetings;
        } catch (error) {
          console.error(`Error fetching meetings for user ${userId}:`, error);
          return [];
        }
      });

      // Wait for all promises to resolve
      const meetingsArrays = await Promise.all(meetingsPromises);

      // Flatten and sort
      const allMeetings = meetingsArrays.flat();
      allMeetings.sort((a, b) => {
        const dateA = new Date(a.meetingDate);
        const dateB = new Date(b.meetingDate);
        return dateA - dateB;
      });

      console.log(`Total meetings fetched: ${allMeetings.length}`);

      // Cache for 24 hours (data only changes on writes, invalidation ensures freshness)
      firebaseCache.set(cacheKey, allMeetings, 24 * 60 * 60 * 1000);

      return allMeetings;
    } catch (error) {
      console.error('❌ Error fetching meetings for multiple users:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const addMeeting = createAsyncThunk(
  "meetings/addMeeting",
  async (meetingData, { rejectWithValue }) => {
    try {
      console.log('📝 Adding meeting');

      // Ensure meetingDate is a proper Firestore Timestamp
      const dataToSave = {
        ...meetingData,
        meetingDate: meetingData.meetingDate, // Already converted to Timestamp in AddMeeting.jsx
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, "meetings"), dataToSave);

      // Mark meetings cache for invalidation (will be cleared on next read)
      firebaseCache.markForInvalidation('meetings');

      // Return serializable data for Redux state
      const serializableData = {
        id: docRef.id,
        ...dataToSave,
        // Convert Timestamp to serializable format
        meetingDate: dataToSave.meetingDate?.seconds ? {
          seconds: dataToSave.meetingDate.seconds,
          nanoseconds: dataToSave.meetingDate.nanoseconds,
        } : dataToSave.meetingDate,
        createdAt: dataToSave.createdAt?.seconds ? {
          seconds: dataToSave.createdAt.seconds,
          nanoseconds: dataToSave.createdAt.nanoseconds,
        } : dataToSave.createdAt,
        updatedAt: dataToSave.updatedAt?.seconds ? {
          seconds: dataToSave.updatedAt.seconds,
          nanoseconds: dataToSave.updatedAt.nanoseconds,
        } : dataToSave.updatedAt
      };

      console.log('✅ Meeting added successfully');
      return serializableData;
    } catch (error) {
      console.error('❌ Error adding meeting:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const updateMeeting = createAsyncThunk(
  "meetings/updateMeeting",
  async ({ id, ...meetingData }, { rejectWithValue }) => {
    try {
      const meetingRef = doc(db, "meetings", id);
      const updateData = {
        ...meetingData,
        updatedAt: Timestamp.now(),
      };
      await updateDoc(meetingRef, updateData);

      // Mark meetings cache for invalidation (will be cleared on next read)
      firebaseCache.markForInvalidation('meetings');

      // Return serializable data for Redux state
      const serializableData = {
        id,
        ...updateData,
        // Convert Timestamp to serializable format if present
        meetingDate: meetingData.meetingDate?.seconds ? {
          seconds: meetingData.meetingDate.seconds,
          nanoseconds: meetingData.meetingDate.nanoseconds,
        } : meetingData.meetingDate,
        createdAt: meetingData.createdAt?.seconds ? {
          seconds: meetingData.createdAt.seconds,
          nanoseconds: meetingData.createdAt.nanoseconds,
        } : meetingData.createdAt,
        updatedAt: meetingData.updatedAt?.seconds ? {
          seconds: meetingData.updatedAt.seconds,
          nanoseconds: meetingData.updatedAt.nanoseconds,
        } : meetingData.updatedAt
      };

      console.log('✅ Meeting updated successfully');
      return serializableData;
    } catch (error) {
      console.error('❌ Error updating meeting:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteMeeting = createAsyncThunk(
  "meetings/deleteMeeting",
  async (meetingId, { rejectWithValue }) => {
    try {
      await deleteDoc(doc(db, "meetings", meetingId));

      // Mark meetings cache for invalidation (will be cleared on next read)
      firebaseCache.markForInvalidation('meetings');

      return meetingId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch single meeting by ID (for detail view)
export const fetchMeetingById = createAsyncThunk(
  "meetings/fetchMeetingById",
  async (meetingId, { rejectWithValue }) => {
    try {
      const cacheKey = `meeting_${meetingId}`;

      // Try cache first
      const cachedData = firebaseCache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      const meetingRef = doc(db, "meetings", meetingId);
      const meetingDoc = await getDoc(meetingRef);

      if (!meetingDoc.exists()) {
        throw new Error('Meeting not found');
      }

      const data = meetingDoc.data();
      let serializableDate;
      if (data.meetingDate?.seconds) {
        serializableDate = {
          seconds: data.meetingDate.seconds,
          nanoseconds: data.meetingDate.nanoseconds,
        };
      } else if (data.meetingDate?.toDate) {
        const timestamp = data.meetingDate;
        serializableDate = {
          seconds: timestamp.seconds,
          nanoseconds: timestamp.nanoseconds,
        };
      } else if (data.meetingDate instanceof Date) {
        const firestoreTimestamp = Timestamp.fromDate(data.meetingDate);
        serializableDate = {
          seconds: firestoreTimestamp.seconds,
          nanoseconds: firestoreTimestamp.nanoseconds,
        };
      } else {
        serializableDate = data.meetingDate;
      }

      const meeting = {
        id: meetingDoc.id,
        ...data,
        meetingDate: serializableDate,
      };

      // Cache for 5 minutes
      firebaseCache.set(cacheKey, meeting, 5 * 60 * 1000);

      return meeting;
    } catch (error) {
      console.error('❌ Error fetching meeting:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// Batch delete meetings
export const batchDeleteMeetings = createAsyncThunk(
  "meetings/batchDeleteMeetings",
  async (meetingIds, { rejectWithValue }) => {
    try {
      const batch = writeBatch(db);

      meetingIds.forEach(meetingId => {
        const meetingRef = doc(db, "meetings", meetingId);
        batch.delete(meetingRef);
      });

      await batch.commit();

      // Clear cache when meetings are deleted
      firebaseCache.clear();

      return meetingIds;
    } catch (error) {
      console.error('❌ Error batch deleting meetings:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const meetingsSlice = createSlice({
  name: "meetings",
  initialState: {
    meetings: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Optimistic update for adding meeting
    optimisticAddMeeting: (state, action) => {
      state.meetings.push(action.payload);
    },
    // Optimistic update for updating meeting
    optimisticUpdateMeeting: (state, action) => {
      const index = state.meetings.findIndex(
        (meeting) => meeting.id === action.payload.id
      );
      if (index !== -1) {
        state.meetings[index] = { ...state.meetings[index], ...action.payload };
      }
    },
    // Optimistic update for deleting meeting
    optimisticDeleteMeeting: (state, action) => {
      state.meetings = state.meetings.filter(
        (meeting) => meeting.id !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch meetings
      .addCase(fetchMeetings.pending, (state) => {
        // Only show loader if we have no data
        if (state.meetings.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchMeetings.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = action.payload;
      })
      .addCase(fetchMeetings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMeetingsForMultipleUsers.pending, (state) => {
        // Only show loader if we have no data
        if (state.meetings.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchMeetingsForMultipleUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = action.payload;
      })
      .addCase(fetchMeetingsForMultipleUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add meeting
      .addCase(addMeeting.pending, (state) => {
        // Don't set loading for add operations to prevent UI flicker
        state.error = null;
      })
      .addCase(addMeeting.fulfilled, (state, action) => {
        state.meetings.push(action.payload);
      })
      .addCase(addMeeting.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Update meeting
      .addCase(updateMeeting.pending, (state) => {
        // Don't set loading for update operations to prevent UI flicker
        state.error = null;
      })
      .addCase(updateMeeting.fulfilled, (state, action) => {
        const index = state.meetings.findIndex(
          (meeting) => meeting.id === action.payload.id
        );
        if (index !== -1) {
          state.meetings[index] = action.payload;
        }
      })
      .addCase(updateMeeting.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Delete meeting
      .addCase(deleteMeeting.pending, (state) => {
        // Don't set loading for delete operations to prevent UI flicker
        state.error = null;
      })
      .addCase(deleteMeeting.fulfilled, (state, action) => {
        state.meetings = state.meetings.filter(
          (meeting) => meeting.id !== action.payload
        );
      })
      .addCase(deleteMeeting.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Fetch meeting by ID
      .addCase(fetchMeetingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeetingById.fulfilled, (state, action) => {
        state.loading = false;
        // Update or add the meeting in the state
        const index = state.meetings.findIndex(
          (meeting) => meeting.id === action.payload.id
        );
        if (index !== -1) {
          state.meetings[index] = action.payload;
        } else {
          state.meetings.push(action.payload);
        }
      })
      .addCase(fetchMeetingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Batch delete meetings
      .addCase(batchDeleteMeetings.pending, (state) => {
        state.error = null;
      })
      .addCase(batchDeleteMeetings.fulfilled, (state, action) => {
        state.meetings = state.meetings.filter(
          (meeting) => !action.payload.includes(meeting.id)
        );
      })
      .addCase(batchDeleteMeetings.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError, optimisticAddMeeting, optimisticUpdateMeeting, optimisticDeleteMeeting } = meetingsSlice.actions;
export default meetingsSlice.reducer;
